import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "routes/constants";
import { useContactInformation } from "hooks/useContactInformation";
import { usePayment } from "hooks/usePayment";
import { useQuote } from "hooks/useQuote";
import { useOffer } from "hooks/useOffer";
import { useLandingPageUrl } from "hooks";
import { useProductForm } from "hooks/contact-information/useProductForm";
import { useContactProgress } from "hooks/contact-information/useContactProgress";
import { useContactCardRefs } from "hooks/contact-information/useContactCardRefs";
import { useContactAutosave } from "hooks/contact-information/useContactAutosave";
import { useContactSubmission } from "hooks/contact-information/useContactSubmission";
import { useGIWLQuoteHydration } from "hooks/contact-information/useGIWLQuoteHydration";
import { useResumeApplicationPrefill } from "hooks/contact-information/useResumeApplicationPrefill";

/**
 * Orchestrates all Contact Information page logic so the component stays purely
 * representational. Composes product-form selection, progress tracking, card
 * refs, per-card autosave, submission, and the product-specific GIWL hydration /
 * resume-prefill effects, returning the flat set of values the JSX renders.
 */
export const useContactInformationPage = () => {
    // ========== Product form + watched data ==========
    const {
        form,
        giwlForm,
        isDTO,
        isMLTA,
        isGIWL,
        contactInfo,
        statementOfHealthData,
        coverageReplacementData,
        policyBeneficiaryData,
        secondaryAddresseeData,
    } = useProductForm();

    // ========== Data hooks ==========
    const { submitContactInformation, isLoadingData, contactData, saveApplicationData, loadSavedApplicationData } =
        useContactInformation();
    const { paymentData } = usePayment();
    const { quoteData, saveQuote } = useQuote();
    const { offerData } = useOffer();
    const landingPageParameters = useLandingPageUrl();
    const landingPageUrl = landingPageParameters?.url ?? "";

    // ========== Computed values ==========
    /** Stable string for GIWL hydration effect — avoid re-running when the parent hook returns a new object each render with the same param Map (e.g. tests). */
    const giwlLandingParamsKey = useMemo(() => {
        const params = landingPageParameters?.params;
        if (!params) return "";
        return Array.from(params.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => `${k}=${String(v)}`)
            .join("&");
    }, [landingPageParameters?.params]);

    // ========== Progress ==========
    useContactProgress({
        contactInfo,
        statementOfHealthData,
        coverageReplacementData,
        policyBeneficiaryData,
        secondaryAddresseeData,
        isDTO,
        isGIWL,
    });

    // ========== Card refs ==========
    const {
        contactInformationRef,
        statementOfHealthRef,
        coverageReplacementRef,
        policyBeneficiaryRef,
        secondaryAddresseeRef,
        cardRefs,
        scrollToCard,
    } = useContactCardRefs({ isDTO, isGIWL, isLoadingData });

    const isNextButtonDisabled = coverageReplacementRef.current?.shouldDisableNext() ?? false;

    // ========== Autosave + submission ==========
    const { handleCardBlur, optimisticSaveStatus } = useContactAutosave({
        cardRefs,
        contactInformationRef,
        statementOfHealthRef,
        coverageReplacementRef,
        policyBeneficiaryRef,
        secondaryAddresseeRef,
        isDTO,
        isMLTA,
        contactData,
        saveApplicationData,
    });

    const { formAction, isPending, handleBack } = useContactSubmission({
        cardRefs,
        contactInformationRef,
        statementOfHealthRef,
        coverageReplacementRef,
        policyBeneficiaryRef,
        secondaryAddresseeRef,
        isDTO,
        isGIWL,
        scrollToCard,
        submitContactInformation,
        paymentToken: paymentData?.paymentToken,
    });

    // ========== Guards ==========
    // DTO/MLTA reach this page with a quote already selected; direct URL access
    // without one goes to the error page. GIWL is exempt — this page is its
    // landing, and useGIWLQuoteHydration owns validation/reset for it.
    const navigate = useNavigate();
    const isMissingRequiredData = !isGIWL && !quoteData;

    useEffect(() => {
        if (isMissingRequiredData) {
            console.error("ContactInformationPage: Missing required data");
            navigate(ROUTES.SOMETHING_WENT_WRONG, { replace: true });
        }
    }, [isMissingRequiredData, navigate]);

    // ========== Product-specific effects ==========
    useGIWLQuoteHydration({ isGIWL, landingPageUrl, giwlLandingParamsKey, giwlForm, saveQuote });

    useResumeApplicationPrefill({
        isDTO,
        isMLTA,
        invitationCode: offerData?.invitationCode,
        applicantType: quoteData?.applicantType,
        loadSavedApplicationData,
    });

    return {
        // Form + flags
        form,
        isDTO,
        isGIWL,
        // Guards
        isMissingRequiredData,
        // Loading / status
        isLoadingData,
        optimisticSaveStatus,
        isNextButtonDisabled,
        isPending,
        // Refs
        contactInformationRef,
        statementOfHealthRef,
        coverageReplacementRef,
        policyBeneficiaryRef,
        secondaryAddresseeRef,
        // Handlers
        handleCardBlur,
        handleBack,
        formAction,
    };
};
